import { supabase } from "../lib/supabase";
import type { Supplier } from "./suppliers";

export type AuditorWithSuppliers = {
  id: string;
  full_name: string;
  email: string | null;
  created_at: string;
  suppliers: Supplier[];
};

/**
 * Fetch all auditors with their assigned suppliers.
 * Used by the ManageAuditors page.
 */
export async function fetchAuditorsWithSuppliers(): Promise<{
  data: AuditorWithSuppliers[];
  hasMore: boolean;
}> {
  // First, fetch all users with role = 'auditor'
  const { data: auditors, error: auditorsError } = await supabase
    .from("users")
    .select(
      `
      id,
      full_name,
      email,
      created_at,
      user_roles!inner(role)
    `,
    )
    .eq("user_roles.role", "auditor")
    .is("deleted_at", null)
    .order("full_name", { ascending: true });

  if (auditorsError) throw auditorsError;

  if (!auditors || auditors.length === 0) {
    return { data: [], hasMore: false };
  }

  // Fetch supplier assignments for all auditors
  const auditorIds = auditors.map((a) => a.id);
  const { data: assignments, error: assignmentsError } = await supabase
    .from("auditor_suppliers")
    .select(
      `
      user_id,
      supplier_id,
      suppliers(id, name, created_at, updated_at)
    `,
    )
    .in("user_id", auditorIds);

  if (assignmentsError) throw assignmentsError;

  // Group suppliers by auditor
  const suppliersByAuditor = new Map<string, Supplier[]>();
  for (const assignment of assignments ?? []) {
    const userId = assignment.user_id;
    if (!suppliersByAuditor.has(userId)) {
      suppliersByAuditor.set(userId, []);
    }
    if (assignment.suppliers) {
      suppliersByAuditor.get(userId)!.push(assignment.suppliers as Supplier);
    }
  }

  // Combine auditors with their suppliers
  const result: AuditorWithSuppliers[] = auditors.map((auditor) => ({
    id: auditor.id,
    full_name: auditor.full_name,
    email: auditor.email,
    created_at: auditor.created_at,
    suppliers: suppliersByAuditor.get(auditor.id) ?? [],
  }));

  return { data: result, hasMore: false };
}

/**
 * Update an auditor's supplier assignments.
 * Performs a full sync: deletes all existing assignments and inserts new ones.
 */
export async function updateAuditorSuppliers(
  userId: string,
  supplierIds: number[],
): Promise<void> {
  // Delete all existing assignments for this auditor
  const { error: deleteError } = await supabase
    .from("auditor_suppliers")
    .delete()
    .eq("user_id", userId);

  if (deleteError) throw deleteError;

  // Insert new assignments (if any)
  if (supplierIds.length > 0) {
    const assignments = supplierIds.map((supplierId) => ({
      user_id: userId,
      supplier_id: supplierId,
    }));

    const { error: insertError } = await supabase
      .from("auditor_suppliers")
      .insert(assignments);

    if (insertError) throw insertError;
  }
}

/**
 * Fetch suppliers by IDs.
 * Used by MultiAutocomplete to display selected suppliers.
 */
export async function fetchSuppliersByIds(ids: number[]): Promise<Supplier[]> {
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .in("id", ids)
    .order("name", { ascending: true });

  if (error) throw error;
  return data ?? [];
}
